import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
  type QueryKey,
} from '@tanstack/react-query';
import service from '../service';
import type { PromoCode } from '../types/promoCode';

type PromoCodesResponse = {
  data: PromoCode[];
  meta?: {
    total: number;
  };
};

type DefaultError = unknown;

export const promoCodeQueryKeys = {
  all: ['promoCodes'] as const,
  lists: () => [...promoCodeQueryKeys.all, 'list'] as const,
  list: (params: Record<string, string>) =>
    [...promoCodeQueryKeys.lists(), params] as const,
  details: () => [...promoCodeQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...promoCodeQueryKeys.details(), id] as const,
};

type UsePromoCodesParams = {
  searchParams?: Record<string, string>;
  enabled?: boolean;
};

export function usePromoCodes<
  TData = PromoCodesResponse,
  TError = DefaultError,
>(
  { searchParams = {}, enabled = true }: UsePromoCodesParams = {},
  options?: Omit<
    UseQueryOptions<PromoCodesResponse, TError, TData, QueryKey>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
): UseQueryResult<TData, TError> {
  const queryKey: QueryKey = promoCodeQueryKeys.list(searchParams);

  return useQuery<PromoCodesResponse, TError, TData>({
    queryKey,
    enabled,
    queryFn: async () => service.getPromoCodes(searchParams),
    ...(options as any),
  });
}
